/**
 * **Period presets** (W22, FR-54, plan D55) — the package's own, in **India
 * time**, for every date filter: the library's *Document date* and *Uploaded*,
 * and the home screen's period.
 *
 * Why not the kit's `PERIOD_LABELS` / `resolvePeriod`: they resolve in the
 * browser's own time zone and have no week and no quarter. Here a day is the
 * IST calendar day (UTC+05:30, no daylight saving), **weeks start on Monday**,
 * and **quarters and years are the Indian financial year's** — April to March,
 * so *This year* in October 2026 is FY 2026-27 and its quarters are April –
 * June, July – September, October – December and January – March.
 *
 * A preset is a name in an address (`?uploaded=this_quarter`), resolved to
 * dates when it is asked, so a saved link keeps meaning *this quarter*. The
 * kit's old names in a saved address still parse: `last_month` is
 * `prev_month`, `this_fy` is `this_year`, `last_fy` is `prev_year`, and
 * `last7` — which has no new name — still means the seven days ending today.
 *
 * Every date here is a `YYYY-MM-DD` string, and every sum is done on UTC
 * midnights, so no browser's clock or zone moves a boundary by a day.
 */

/** FR-54's presets. `custom` is a from–to pair the person types. */
export type IstPeriodPreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'prev_week'
  | 'this_month'
  | 'prev_month'
  | 'this_quarter'
  | 'prev_quarter'
  | 'this_year'
  | 'prev_year'
  | 'custom'

/** The kit's names, as a saved address may still carry them. */
export type LegacyPeriodPreset = 'last7' | 'last_month' | 'this_fy' | 'last_fy'

/** In the order the picker offers them (FR-54): this period first, then the one before, then a custom range. */
export const PERIOD_PRESETS: readonly IstPeriodPreset[] = [
  'today',
  'this_week',
  'this_month',
  'this_quarter',
  'this_year',
  'yesterday',
  'prev_week',
  'prev_month',
  'prev_quarter',
  'prev_year',
  'custom',
]

export const PERIOD_PRESET_LABELS: Record<IstPeriodPreset | 'last7', string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This week',
  prev_week: 'Previous week',
  this_month: 'This month',
  prev_month: 'Previous month',
  this_quarter: 'This quarter',
  prev_quarter: 'Previous quarter',
  this_year: 'This year',
  prev_year: 'Previous year',
  custom: 'Custom',
  last7: 'Last 7 days',
}

/** The kit's old names → today's; `last7` keeps its own meaning. */
const LEGACY: Record<LegacyPeriodPreset, IstPeriodPreset | 'last7'> = {
  last7: 'last7',
  last_month: 'prev_month',
  this_fy: 'this_year',
  last_fy: 'prev_year',
}

/** A preset, resolved: the IST days it covers, both ends included. */
export interface IstPeriod {
  preset: IstPeriodPreset | 'last7'
  from: string
  to: string
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000

/** `YYYY-MM-DD` that is a real day — `2026-02-30` is not. */
export function isIsoDate(value: unknown): value is string {
  const m = typeof value === 'string' ? ISO_DATE.exec(value) : null
  if (!m) return false
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3])
}

/** The IST calendar day an instant falls on. */
export function istToday(now: Date = new Date()): string {
  return new Date(now.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10)
}

const utc = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d))
}
const iso = (d: Date) => d.toISOString().slice(0, 10)
const day = (y: number, monthIndex: number, d: number) => iso(new Date(Date.UTC(y, monthIndex, d)))

/** `iso` moved by `n` days. */
export function addDays(date: string, n: number): string {
  const d = utc(date)
  d.setUTCDate(d.getUTCDate() + n)
  return iso(d)
}

/** The Indian financial year a day falls in, as `2026-27`, with its first and last day. */
export function financialYearOf(date: string, back = 0): { label: string; from: string; to: string } {
  const d = utc(date)
  const start = (d.getUTCMonth() >= 3 ? d.getUTCFullYear() : d.getUTCFullYear() - 1) - back
  return { label: `${start}-${String((start + 1) % 100).padStart(2, '0')}`, from: day(start, 3, 1), to: day(start + 1, 2, 31) }
}

/** A name from an address — today's, or the kit's old one read as today's. Anything else is `null`. */
export function parsePeriodPreset(value: string | null | undefined): IstPeriodPreset | 'last7' | null {
  if (!value) return null
  if ((PERIOD_PRESETS as readonly string[]).includes(value)) return value as IstPeriodPreset
  return LEGACY[value as LegacyPeriodPreset] ?? null
}

/** The day `today` names: a `YYYY-MM-DD` as given, an instant as its IST day. */
const todayOf = (today: string | Date | undefined): string => (typeof today === 'string' && isIsoDate(today) ? today : istToday(today instanceof Date ? today : new Date()))

/**
 * A preset's days as of `today` (an IST `YYYY-MM-DD`, or an instant — now, by
 * default). `custom` starts as today alone; a person then types the range.
 */
export function resolveIstPeriod(preset: IstPeriodPreset | LegacyPeriodPreset, today?: string | Date): IstPeriod {
  const t = todayOf(today)
  const name = parsePeriodPreset(preset) ?? 'custom'
  const d = utc(t)
  const y = d.getUTCFullYear()
  const m = d.getUTCMonth()
  // Monday is the first day of the week: Sunday (0) is six days after it.
  const monday = addDays(t, -((d.getUTCDay() + 6) % 7))
  // The Indian FY's quarters are the calendar's, counted from April.
  const quarter = m - (m % 3)
  switch (name) {
    case 'today':
      return { preset: name, from: t, to: t }
    case 'yesterday': {
      const y1 = addDays(t, -1)
      return { preset: name, from: y1, to: y1 }
    }
    case 'last7':
      return { preset: name, from: addDays(t, -6), to: t }
    case 'this_week':
      return { preset: name, from: monday, to: addDays(monday, 6) }
    case 'prev_week':
      return { preset: name, from: addDays(monday, -7), to: addDays(monday, -1) }
    case 'this_month':
      return { preset: name, from: day(y, m, 1), to: day(y, m + 1, 0) }
    case 'prev_month':
      return { preset: name, from: day(y, m - 1, 1), to: day(y, m, 0) }
    case 'this_quarter':
      return { preset: name, from: day(y, quarter, 1), to: day(y, quarter + 3, 0) }
    case 'prev_quarter':
      return { preset: name, from: day(y, quarter - 3, 1), to: day(y, quarter, 0) }
    case 'this_year': {
      const fy = financialYearOf(t)
      return { preset: name, from: fy.from, to: fy.to }
    }
    case 'prev_year': {
      const fy = financialYearOf(t, 1)
      return { preset: name, from: fy.from, to: fy.to }
    }
    case 'custom':
      return { preset: name, from: t, to: t }
  }
}

/** A date filter's value: *any time* (where offered), a preset, or a custom range typed as `YYYY-MM-DD`. */
export interface PeriodValue {
  preset: 'any' | IstPeriodPreset | LegacyPeriodPreset
  /** `custom` only. */
  from: string
  to: string
}

/** Why a custom range is refused — the start after the end — or `null` when it may be asked. One end alone is an open range. */
export function customPeriodError(from: string, to: string): string | null {
  if (from && !isIsoDate(from)) return 'The start is not a date.'
  if (to && !isIsoDate(to)) return 'The end is not a date.'
  if (from && to && from > to) return 'The start is after the end. Choose a start on or before the end.'
  return null
}

/**
 * The IST days a filter asks for as of `today` — `null` for *any time*, for a
 * custom range with neither end, and for one the picker would refuse (a start
 * after its end, from a hand-made address), so nothing false is ever asked.
 */
export function periodRange(value: PeriodValue, today?: string | Date): { from: string; to: string } | null {
  if (value.preset === 'any') return null
  if (value.preset === 'custom') {
    if (!value.from && !value.to) return null
    if (customPeriodError(value.from, value.to)) return null
    return { from: value.from, to: value.to }
  }
  // A name nobody knows asks nothing rather than something false.
  const preset = parsePeriodPreset(value.preset)
  if (!preset) return null
  const { from, to } = resolveIstPeriod(preset, today)
  return { from, to }
}
